// What each plan actually gives you.
//
// One source of truth for every surface that describes the plans, so the
// pricing modal, the FAQ and anything added later cannot drift apart — or
// drift away from the code. Every line below corresponds to a limit that is
// enforced server-side today:
//
//   swipes/day        matching.service.ts   DAILY_SWIPE_LIMITS
//   keep pile         matching.service.ts   KEEP_PILE_LIMITS
//   photos + videos   photos.service.ts     PROFILE_MEDIA_LIMITS
//   Moments/day       moments.service.ts    DAILY_MOMENT_LIMITS
//   create a room     community.routes.ts   requireTier(['premium_plus'])
//   messaging         messaging.service.ts  subscription check
//
// CHANGING A NUMBER HERE CHANGES NOTHING. These are the words; the limits are
// in the services listed above. Change both together.
//
// Nothing that is not built appears here. The modal used to advertise "See who
// liked you", "Read receipts", "Advanced filters", "Unlimited super likes",
// "Profile boost", "See who viewed your profile", "Advanced analytics", "Video
// call features" and "Exclusive events access" — none of which exist. Selling
// them is what makes a chargeback defensible for the customer.

export interface PlanLimits {
  swipesPerDay: number | null;
  keepPile: number;
  photos: number | null;
  videos: number | null;
  momentsPerDay: number | null;
}

export const PLAN_LIMITS: Record<'free' | 'premium' | 'premium_plus', PlanLimits> = {
  free: { swipesPerDay: 10, keepPile: 5, photos: 3, videos: 1, momentsPerDay: 3 },
  premium: { swipesPerDay: 20, keepPile: 15, photos: 5, videos: 3, momentsPerDay: 10 },
  premium_plus: { swipesPerDay: null, keepPile: 20, photos: null, videos: null, momentsPerDay: null },
};

/** What every signed-in account gets without paying. Shown for comparison. */
export const FREE_FEATURES: string[] = [
  '10 swipes a day',
  '5 cards in your Keep Pile',
  '3 photos and 1 profile video',
  '3 Moments a day',
  'Message Bet conversations',
  'Read, post, comment and vote in every chat room',
];

export const PREMIUM_FEATURES: string[] = [
  'Message anyone — not just through Message Bets',
  '20 swipes a day, double the free 10',
  '15 cards in your Keep Pile, up from 5',
  '5 photos and 3 profile videos',
  '10 Moments a day, up from 3',
  'Priority customer support',
];

export const PREMIUM_PLUS_FEATURES: string[] = [
  'Everything in Premium',
  'Unlimited swipes, every day',
  '20 cards in your Keep Pile',
  'Unlimited profile photos and videos',
  'Unlimited Moments',
  'Create your own chat rooms',
];

/** One-line summary of what separates the tiers, for short copy. */
export const PLAN_TAGLINES: Record<'premium' | 'premium_plus', string> = {
  premium: 'More of everything, and message anyone.',
  premium_plus: 'No limits, and rooms of your own.',
};

// ---------------------------------------------------------------------------
// What each plan card offers a given account.
//
// Pure functions rather than inline JSX conditions so the matrix — four
// states across three tiers — can be tested directly. The modal used to
// ignore the question entirely and show "Start 60-Day Trial" on both cards,
// including to someone already paying for that exact plan.
// ---------------------------------------------------------------------------

export type PlanId = 'premium' | 'premium_plus';

export type PlanCtaState =
  /** The account is on this plan. */
  | 'current'
  /** A higher plan already contains it — nothing to buy. */
  | 'included'
  /** A paying account can move up to this plan. */
  | 'upgrade'
  /** No subscription: this is a fresh purchase. */
  | 'subscribe';

export function planCtaState(
  planId: PlanId,
  currentTier: PlanId | null,
  isPremium: boolean,
): PlanCtaState {
  // isPremium, not the tier alone: a tier left on a lapsed row must not read
  // as a live plan, or an expired subscriber sees "Your current plan" and no
  // way to buy anything.
  if (!isPremium || !currentTier) return 'subscribe';
  if (currentTier === planId) return 'current';
  if (currentTier === 'premium_plus') return 'included';
  return 'upgrade';
}

export function planCtaLabel(
  state: PlanCtaState,
  planName: string,
  options: { processing?: boolean; cancelAtPeriodEnd?: boolean } = {},
): string {
  if (options.processing) return 'Redirecting to secure checkout…';
  switch (state) {
    case 'current': return options.cancelAtPeriodEnd ? 'Ending soon — your plan' : 'Your current plan';
    case 'included': return 'Included in your plan';
    case 'upgrade': return `Upgrade to ${planName}`;
    default: return 'Start 60-Day Trial';
  }
}

/** Only a fresh purchase gets the introductory price. */
export function showsIntroPrice(state: PlanCtaState): boolean {
  return state === 'subscribe';
}

/** Whether the card's button does anything. */
export function planCtaEnabled(state: PlanCtaState): boolean {
  return state === 'subscribe' || state === 'upgrade';
}
